import React from 'react';

import SearchableSelect from 'app/src/elements/SearchableSelect';
import { openPickerUnlessDateTextClick } from 'app/src/elements/DateInput';
import * as Colors from 'app/src/styles/colors';

// Reusable building blocks for an inline (non-modal) filter bar: a contained
// panel, a responsive field grid, and label-over-control fields that line up.
// Layout uses CSS grid, so these render as web divs rather than RN views.

const control = {
  height: '32px',
  fontSize: '13px',
  padding: '0 6px',
  border: `1px solid ${Colors.iconGray}`,
  borderRadius: '5px',
  boxSizing: 'border-box',
  backgroundColor: '#ffffff',
  width: '100%',
};

export const filterStyles = {
  container: {
    width: '100%',
    padding: '16px',
    marginBottom: '12px',
    backgroundColor: Colors.elLightLightGray,
    border: `1px solid ${Colors.elGray}`,
    borderRadius: '6px',
    boxSizing: 'border-box',
  },
  // auto-fit keeps every column at least 150px, so a typical six-field row
  // stays on one line once the panel is ~900px wide and wraps below that.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px 16px',
    alignItems: 'start',
  },
  advancedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '12px 16px',
    alignItems: 'start',
    marginTop: '16px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '14px',
  },
  linkButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    fontSize: '13px',
    fontWeight: '600',
    color: Colors.mediumHighlight,
    cursor: 'pointer',
  },
  control,
};

const styles = {
  field: { display: 'flex', flexDirection: 'column' },
  label: {
    marginBottom: '4px',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '16px',
    color: Colors.darkText,
  },
  rangeRow: { display: 'flex', gap: '8px' },
  rangeInput: { ...control, flex: 1, minWidth: 0 },
};

export const FilterField = ({ label, style, children }) => (
  <div style={style ? { ...styles.field, ...style } : styles.field}>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);

export const FilterTextInput = ({ label, value, onChange }) => (
  <FilterField label={label}>
    <input
      type="text"
      aria-label={label}
      style={control}
      value={value || ''}
      onChange={event => onChange(event.target.value)}
    />
  </FilterField>
);

export const FilterSelect = ({ label, options, value, onChange }) => (
  <FilterField label={label}>
    <SearchableSelect
      placeholder={`Select ${label}`}
      ariaLabel={label}
      options={options}
      value={value || ''}
      onChange={onChange}
    />
  </FilterField>
);

// A plain native <select> for short, fixed option sets. The caller supplies
// the <option> children.
export const FilterNativeSelect = ({ label, value, onChange, children }) => (
  <FilterField label={label}>
    <select
      aria-label={label}
      style={control}
      value={value || ''}
      onChange={event => onChange(event.target.value)}
    >
      {children}
    </select>
  </FilterField>
);

// A min/max pair. type="date" swaps the number inputs for date pickers and
// labels the bounds From/To instead of Min/Max.
export const FilterRange = ({
  label, type = 'number', wide = false,
  minValue, maxValue, onChangeMin, onChangeMax,
}) => {
  const isDate = 'date' === type;
  const [minBound, maxBound] = isDate ? ['From', 'To'] : ['Min', 'Max'];

  const boundInput = (bound, value, onChange) => (
    <input
      type={type}
      aria-label={`${label} ${bound}`}
      placeholder={isDate ? undefined : bound}
      style={styles.rangeInput}
      value={value || ''}
      onClick={isDate ? openPickerUnlessDateTextClick : undefined}
      onChange={event => onChange(event.target.value)}
    />
  );

  return (
    <FilterField label={label} style={wide ? { gridColumn: 'span 2' } : null}>
      <div style={styles.rangeRow}>
        {boundInput(minBound, minValue, onChangeMin)}
        {boundInput(maxBound, maxValue, onChangeMax)}
      </div>
    </FilterField>
  );
};
